import { createContext, useContext } from 'react'
import type { AppRole, Profile } from '../types/db'

export interface AuthResult {
  ok: boolean
  error?: string
}

export interface AuthState {
  status: 'loading' | 'signedOut' | 'signedIn'
  userId: string | null
  profile: Profile | null
  role: AppRole | null
  /** True until a newly created account has chosen its own password. */
  mustChangePassword: boolean
  /** True when running on the bundled demo dataset instead of a real project. */
  demoMode: boolean
  /**
   * True from a tap on Sign out until the next sign-in. RequireAuth reads it so
   * a deliberate exit does not remember the page for whoever signs in next;
   * only an interrupted session (token expiry) is worth resuming.
   */
  signedOutByUser: boolean
  signIn: (phone: string, password: string) => Promise<AuthResult>
  signOut: () => Promise<void>
  changePassword: (nextPassword: string) => Promise<AuthResult>
  refreshProfile: () => Promise<void>
}

export const AuthContext = createContext<AuthState | null>(null)

export function useAuth(): AuthState {
  const value = useContext(AuthContext)
  if (!value) throw new Error('useAuth must be used inside <AuthProvider>')
  return value
}

/** Michel, or a coach he has delegated approval to. Mirrors is_approver() in SQL. */
export function isApprover(profile: Profile | null): boolean {
  if (!profile) return false
  return profile.role === 'admin' || profile.is_delegated_approver
}

/** Everyone who gets the admin shell rather than the coach tab bar. */
export function usesAdminShell(role: AppRole | null): boolean {
  return role === 'admin' || role === 'finance' || role === 'materials'
}

/** Where each role lands after signing in. */
export function homePathForRole(role: AppRole | null): string {
  return usesAdminShell(role) ? '/admin' : '/'
}
